import { useState, useRef, useCallback, useEffect, ReactNode } from 'react'
import { TableCell, Box, useTheme, type SxProps, type Theme } from '@mui/material'

interface ResizableTableCellProps {
  children: ReactNode
  minWidth?: number
  width?: number | string
  /** When set, pins this header cell below the main table header while scrolling. */
  stickyTop?: number
  sx?: any
  [key: string]: any
}

export function ResizableTableCell({ 
  children, 
  minWidth = 50, 
  width: initialWidth,
  stickyTop,
  sx = {},
  ...props 
}: ResizableTableCellProps) {
  const theme = useTheme()
  const [width, setWidth] = useState<number | undefined>(
    typeof initialWidth === 'number' ? initialWidth : undefined
  )
  const [isResizing, setIsResizing] = useState(false)
  const cellRef = useRef<HTMLTableCellElement>(null)
  const startXRef = useRef<number>(0)
  const startWidthRef = useRef<number>(0)
  const columnIndexRef = useRef<number>(-1)
  const tableRef = useRef<HTMLTableElement | null>(null)

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation() // Stop all event propagation immediately
    if (cellRef.current) {
      setIsResizing(true)
      startXRef.current = e.clientX
      startWidthRef.current = cellRef.current.offsetWidth
      
      // Store table and column index for use during resize
      const table = cellRef.current.closest('table') as HTMLTableElement | null
      tableRef.current = table
      if (table && cellRef.current.parentElement) {
        columnIndexRef.current = Array.from(cellRef.current.parentElement.children).indexOf(cellRef.current)
      }
      
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.body.style.pointerEvents = 'none' // Prevent other interactions during resize
      // Re-enable pointer events on the resize handle
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.style.pointerEvents = 'auto'
      }
    }
  }, [])

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!cellRef.current) return

    // Find the table and get all cells in this column
    const table = cellRef.current.closest('table')
    if (!table) return

    const cellIndex = Array.from(cellRef.current.parentElement?.children || []).indexOf(cellRef.current)
    if (cellIndex === -1) return

    // Get all rows (header and body)
    const rows = Array.from(table.querySelectorAll('tr'))
    let maxWidth = minWidth

    // Measure each cell in this column
    rows.forEach((row) => {
      const cells = Array.from(row.children)
      const cell = cells[cellIndex] as HTMLElement
      if (cell) {
        // Temporarily remove width constraints to measure natural width
        const originalWidth = cell.style.width
        const originalMinWidth = cell.style.minWidth
        const originalMaxWidth = cell.style.maxWidth
        const originalWhiteSpace = cell.style.whiteSpace
        
        cell.style.width = 'auto'
        cell.style.minWidth = 'auto'
        cell.style.maxWidth = 'none'
        cell.style.whiteSpace = 'nowrap'
        
        // Measure the content width
        const contentWidth = cell.scrollWidth
        
        // Add padding (left + right)
        const padding = parseInt(getComputedStyle(cell).paddingLeft) + parseInt(getComputedStyle(cell).paddingRight)
        const totalWidth = contentWidth + padding + 20 // Add extra 20px for the resize handle area
        
        maxWidth = Math.max(maxWidth, totalWidth)
        
        // Restore original styles
        cell.style.width = originalWidth
        cell.style.minWidth = originalMinWidth
        cell.style.maxWidth = originalMaxWidth
        cell.style.whiteSpace = originalWhiteSpace
      }
    })

    // Set the calculated width for all cells in this column
    rows.forEach((row) => {
      const cells = Array.from(row.children)
      const cell = cells[cellIndex] as HTMLElement
      if (cell) {
        cell.style.width = `${maxWidth}px`
        cell.style.minWidth = `${minWidth}px` // Keep minWidth at minimum, not maxWidth
        cell.style.maxWidth = 'none' // Remove maxWidth constraint to allow resizing smaller
      }
    })

    // Update state
    setWidth(maxWidth)
  }, [minWidth])

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startXRef.current
      const newWidth = Math.max(minWidth, startWidthRef.current + diff)
      setWidth(newWidth)
      
      // Also update all cells in the same column in real-time during resize
      if (tableRef.current && columnIndexRef.current !== -1) {
        const rows = Array.from(tableRef.current.querySelectorAll('tr'))
        rows.forEach((row) => {
          const cells = Array.from(row.children)
          const cell = cells[columnIndexRef.current] as HTMLElement
          if (cell) {
            cell.style.width = `${newWidth}px`
            cell.style.minWidth = `${minWidth}px`
            cell.style.maxWidth = 'none'
          }
        })
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.body.style.pointerEvents = ''
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.body.style.pointerEvents = ''
    }
  }, [isResizing, minWidth])

  // Update the cell width when state changes and sync with all cells in the same column
  useEffect(() => {
    if (cellRef.current && width) {
      // Update this cell
      cellRef.current.style.width = `${width}px`
      cellRef.current.style.minWidth = `${minWidth}px`
      cellRef.current.style.maxWidth = 'none'
      
      // Find the table and get all cells in this column
      const table = cellRef.current.closest('table')
      if (table) {
        const cellIndex = Array.from(cellRef.current.parentElement?.children || []).indexOf(cellRef.current)
        if (cellIndex !== -1) {
          // Update all cells in the same column (header and body)
          const rows = Array.from(table.querySelectorAll('tr'))
          rows.forEach((row) => {
            const cells = Array.from(row.children)
            const cell = cells[cellIndex] as HTMLElement
            if (cell && cell !== cellRef.current) {
              cell.style.width = `${width}px`
              cell.style.minWidth = `${minWidth}px`
              cell.style.maxWidth = 'none'
            }
          })
        }
      }
    }
  }, [width, minWidth])

  // Check if this cell is in a sticky header (via useEffect after mount)
  const [isInStickyHeader, setIsInStickyHeader] = useState(false)
  
  useEffect(() => {
    if (cellRef.current) {
      const thead = cellRef.current.closest('thead')
      if (thead) {
        const computedStyle = window.getComputedStyle(thead)
        if (computedStyle.position === 'sticky') {
          setIsInStickyHeader(true)
        }
      }
    }
  }, [])

  const isExplicitSticky = stickyTop !== undefined
  const isSticky = isExplicitSticky || isInStickyHeader

  return (
    <TableCell
      ref={cellRef}
      {...props}
      sx={{
        position: isSticky ? 'sticky' : 'relative',
        top: isExplicitSticky ? stickyTop : isInStickyHeader ? 0 : undefined,
        zIndex: isExplicitSticky ? 9 : isInStickyHeader ? 10 : undefined,
        backgroundColor: isSticky ? 'background.paper' : undefined,
        width: width ? `${width}px` : initialWidth,
        minWidth: minWidth ? `${minWidth}px` : (typeof initialWidth === 'number' ? initialWidth : undefined),
        maxWidth: 'none', // Allow resizing smaller after auto-fit
        paddingRight: '12px !important', // Make room for resize handle
        ...sx,
      }}
    >
      {children}
      <Box
        data-resize-handle="true" // Marker to identify resize handles
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
          e.nativeEvent.stopImmediatePropagation() // Stop all event propagation immediately
          handleMouseDown(e)
        }}
        onDoubleClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          e.nativeEvent.stopImmediatePropagation()
          handleDoubleClick(e)
        }}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          e.nativeEvent.stopImmediatePropagation()
        }}
        sx={{
          position: 'absolute',
          top: 0,
          right: '-2px', // Reduced from -4px to minimize extension beyond cell
          width: '6px', // Reduced from 10px to minimize overlap
          height: '100%',
          cursor: 'col-resize',
          backgroundColor: 'transparent',
          zIndex: 10,
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Ensure it doesn't extend beyond table boundaries
          overflow: 'hidden',
          // Prevent any interaction with elements outside the table
          isolation: 'isolate',
          '&:hover': {
            backgroundColor: theme.palette.primary.main,
            opacity: 0.4,
          },
          '&:active': {
            backgroundColor: theme.palette.primary.main,
            opacity: 0.7,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            right: '1px',
            top: '20%',
            bottom: '20%',
            width: '1px',
            backgroundColor: theme.palette.divider,
            opacity: 0.5,
          },
        }}
      />
    </TableCell>
  )
}

/** Pin the actions column on the right while horizontally scrolling wide tables. */
export const stickyActionsHeaderSx: SxProps<Theme> = {
  position: 'sticky',
  right: 0,
  zIndex: 11,
  bgcolor: 'background.paper',
  borderLeft: 1,
  borderColor: 'divider',
}

export const stickyActionsBodySx: SxProps<Theme> = {
  position: 'sticky',
  right: 0,
  zIndex: 1,
  bgcolor: 'background.paper',
  borderLeft: 1,
  borderColor: 'divider',
}
